// Three accessibility gaps in MUD 1.2's `mud-sidebar`, closed from the outside.
// Each one is a fact about the component's shadow DOM, read from
// `mud-sidebar-item.tsx` / `mud-sidebar-group.tsx` / `mud-sidebar.css`, and each
// is a candidate for an upstream request (docs/shape-ops-mud-1-2-migration.md):
//
//   1. The icon-only rail hides the label (`:host([collapsed]) .label` is
//      `display: none`) and the icon is `aria-hidden`, so the row's link has no
//      accessible name. The row says what its name is in `data-accessible-name`
//      and this puts it on the inner control as `aria-label`.
//   2. A group's heading is a plain `<div class="heading">`, not a heading, so
//      the rail's sections are not reachable by heading navigation as they were
//      when they were `<h2>`s. It gets `role="heading"` and `aria-level="2"`.
//   3. A row that carries its own link beside it (the alerts indicator, NC-MENU-18)
//      needs both in one list item: a link that is a direct child of `role="list"`
//      is an axe `aria-required-children` violation. The wrapper is the list item
//      (`role="listitem"`) and the row's host, marked `data-overlay-link`, gives its
//      own `listitem` role up (`presentation`) so no list item nests in another.
//
// Only attributes the component does not render are written, so its own re-render
// never undoes them; a MutationObserver re-applies them if the inner control is
// replaced. The attributes are standard ARIA on the inner nodes: no API is
// invented, and nothing here changes a width, a colour or a token.

const ITEM = 'mud-sidebar-item';
const GROUP = 'mud-sidebar-group';

function applyItem(host: HTMLElement): void {
  const name = host.getAttribute('data-accessible-name');
  const control = host.shadowRoot?.querySelector<HTMLElement>('[part="item"]');
  if (control) {
    if (name) control.setAttribute('aria-label', name);
    else control.removeAttribute('aria-label');
  }
  if (host.hasAttribute('data-overlay-link')) host.setAttribute('role', 'presentation');
  else if (host.getAttribute('role') === 'presentation') host.setAttribute('role', 'listitem');
}

function applyGroup(host: HTMLElement): void {
  const heading = host.shadowRoot?.querySelector<HTMLElement>('.heading');
  if (heading) {
    heading.setAttribute('role', 'heading');
    heading.setAttribute('aria-level', '2');
  }
}

function watch(host: HTMLElement, apply: (host: HTMLElement) => void): () => void {
  apply(host);
  const root = host.shadowRoot;
  if (!root || typeof MutationObserver === 'undefined') return () => undefined;
  const observer = new MutationObserver(() => apply(host));
  observer.observe(root, { childList: true, subtree: true });
  return () => observer.disconnect();
}

/**
 * Applies the three fixes to every sidebar row and group under `container`, now and
 * whenever the component finishes (re)rendering. Returns the cleanup.
 */
export function patchSidebarA11y(container: HTMLElement): () => void {
  const stops: Array<() => void> = [];
  container.querySelectorAll<HTMLElement>(ITEM).forEach((el) => stops.push(watch(el, applyItem)));
  container.querySelectorAll<HTMLElement>(GROUP).forEach((el) => stops.push(watch(el, applyGroup)));
  return () => stops.forEach((stop) => stop());
}
